/**
 * Static data for the Mobilversion hero module — 1:1 port of the prototype's
 * `mobilInbox` / `mobilDocs` / `mobilChat` arrays (Hausverwaltung Hero.dc.html,
 * lines 2543–2567). Kept in a sibling file so the phone renderers stay under
 * the line budget.
 */

import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";

/** A single Inbox conversation row (phone 1). */
export interface MobilInboxRow {
  readonly initial: string;
  readonly bg: string;
  readonly fg: string;
  readonly name: string;
  readonly sub: string;
  readonly time: string;
}

/** A single Dokumente card (phone 2). `isText` vs `isTable` picks the thumbnail. */
export interface MobilDocCard {
  readonly name: string;
  readonly isText: boolean;
  readonly isTable: boolean;
}

/** A Team-Chat feed entry — either a date divider or a message (phone 3). */
export type MobilChatEntry =
  | { readonly kind: "date"; readonly label: string }
  | {
      readonly kind: "msg";
      readonly initial: string;
      readonly bg: string;
      readonly name: string;
      readonly time: string;
      readonly lines: readonly string[];
    };

/** `t` is `useTranslations("marketing.moduleMobilData")`. */
export function mobilInbox(t: Translate): readonly MobilInboxRow[] {
  return [
    {
      initial: "B",
      bg: ld("#DDE7F2", "#303942"),
      fg: ld("#3F6CB0", "#7cacf5"),
      name: t("familieBrandt"),
      sub: t("rueckfrageNebenkostenabrechnung2025"),
      time: t("k8Min"),
    },
    {
      initial: "A",
      bg: ld("#EADFCB", "#453b29"),
      fg: ld("#8A6D3B", "#c5a673"),
      name: t("aKSicherheitstechnik"),
      sub: t("angebotSchliessanlageLindenhofPdf"),
      time: t("k1Std"),
    },
    {
      initial: "W",
      bg: ld("#D9E7DE", "#323e36"),
      fg: ld("#3A7D5A", "#79bc96"),
      name: t("wegAhornweg5"),
      sub: t("formularWohnungsuebergabeAbgesendet"),
      time: t("k1T"),
    },
    {
      initial: "S",
      bg: ld("#E4DCEC", "#413a48"),
      fg: ld("#7A6CB0", "#ac9fe7"),
      name: t("stadtwerkeMuenchen"),
      sub: t("zaehlerstandStromAblesungFaellig"),
      time: t("k2T"),
    },
    {
      initial: "P",
      bg: ld("#F1DDDD", "#473636"),
      fg: ld("#B65C5C", "#eb8c8a"),
      name: t("petraSommer"),
      sub: t("schluesseluebergabeTerminvorschlag"),
      time: t("k3T"),
    },
    {
      initial: "N",
      bg: ld("#DDE7F2", "#303942"),
      fg: ld("#3F6CB0", "#7cacf5"),
      name: t("hausmeisterNowak"),
      sub: t("treppenhausreinigungErledigt"),
      time: t("k4T"),
    },
    {
      initial: "K",
      bg: ld("#EADFCB", "#453b29"),
      fg: ld("#8A6D3B", "#c5a673"),
      name: t("tKrueger"),
      sub: t("besichtigungBirkenallee3"),
      time: t("k5T"),
    },
  ];
}

/** `t` is `useTranslations("marketing.moduleMobilData")`. */
export function mobilDocs(t: Translate): readonly MobilDocCard[] {
  return [
    { name: t("wohnungsuebergabeAhornweg5Pdf"), isText: true, isTable: false },
    { name: t("nebenkostenabrechnung2025Pdf"), isText: false, isTable: true },
    { name: t("wartungsvertragAufzugPdf"), isText: true, isTable: false },
    { name: t("teilungserklaerungLindenhofPdf"), isText: true, isTable: false },
    { name: t("protokollEtv2025Pdf"), isText: true, isTable: false },
    { name: t("versicherungGebaeude2025Pdf"), isText: false, isTable: true },
  ];
}

/** `t` is `useTranslations("marketing.moduleMobilData")`. */
export function mobilChat(t: Translate): readonly MobilChatEntry[] {
  return [
    { kind: "date", label: t("gestern") },
    {
      kind: "msg",
      initial: "SH",
      bg: "#E8772E",
      name: t("sandraHoltkamp"),
      time: "09:14",
      lines: [t("moinZusammenEtvLindenhof")],
    },
    {
      kind: "msg",
      initial: "JB",
      bg: "#3F6CB0",
      name: t("jensBauer"),
      time: "09:21",
      lines: [t("topIchKuemmereMich"), t("tagesordnungHaengeIchGleich")],
    },
    { kind: "date", label: t("heute") },
    {
      kind: "msg",
      initial: "MY",
      bg: "#3A7D5A",
      name: t("mehmetYldz"),
      time: "08:02",
      lines: [t("aufzugNotdienstDaStoerung")],
    },
    {
      kind: "msg",
      initial: "SH",
      bg: "#E8772E",
      name: t("sandraHoltkamp"),
      time: "08:09",
      lines: [t("superDanke")],
    },
  ];
}
